import React from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { handleUnauthorizedResponse } from '../../../api/http';
import { AuthData } from '../../../context/auth';
import { PolicyData, withPolicyContext } from '../../../context/policy';
import { Exposure, ExposureCharacteristics } from '../../../types/policy';
import {
	getActiveCharacteristics,
	getEntityNameFrom_DescriptionField,
	isPolicyEditable,
} from '../../../util/util';
import {
	StyledSectionContainer,
	StyledTitle,
	StyledTitleContainer,
} from '../../common';
import { errorToast, successToast } from '../../Toast';
import ConfirmationModal from '../ConfirmationModal';
import AddExposureModal from './AddExposureModal';
import ExposuresList from './ExposuresList';

interface Props {
	authData: AuthData;
	policyData: PolicyData;
}

const Exposures = (props: Props) => {
	const { t } = useTranslation();
	const { getValues, formState, reset } = useFormContext();
	const { dirtyFields } = formState;
	const [isConfirmModalOpen, setIsConfirmModalOpen] = React.useState(false);
	const [removedExposure, setRemoveExposure] = React.useState(null);
	const { authData, policyData } = props;

	const { productConfig, selectedQuote, policy, saveFastForm, setLoading } =
		policyData;

	const exposures =
		!policy.issuedTimestamp && selectedQuote
			? selectedQuote.exposures
			: policy.exposures;
	const policyConfig = productConfig.policyConfiguration;
	const isInEditMode =
		authData.hasPermission('quote.update') &&
		isPolicyEditable(policy, selectedQuote);

	const removeExposure = async () => {
		setIsConfirmModalOpen(false);
		setLoading(true);
		try {
			await saveFastForm(getValues(), dirtyFields, reset, {
				removeExposure: { exposureLocator: removedExposure.locator },
			});
			successToast('Successfully removed exposure');
		} catch (error) {
			errorToast('Error removing exposure');
			if (await handleUnauthorizedResponse(error, authData)) {
				return;
			}
		} finally {
			setLoading(false);
		}
	};

	const renderActions = () => {
		return (
			<AddExposureModal
				exposureConfigs={policyConfig.exposures}
				policyData={policyData}
				authData={authData}
			/>
		);
	};

	const renderRemoveExposureModal = () => {
		const activeCharacteristics = getActiveCharacteristics(
			removedExposure.characteristics,
		).pop() as ExposureCharacteristics;
		return (
			<ConfirmationModal
				entityName={
					removedExposure.exposureName ||
					getEntityNameFrom_DescriptionField(activeCharacteristics) ||
					removedExposure.name
				}
				entityType={t('exposure.singular.lower')}
				authToken={authData.auth.authorizationToken}
				onRemove={() => removeExposure()}
				cancel={() => setIsConfirmModalOpen(false)}
			/>
		);
	};

	const openConfirmationModal = (exposure: Exposure) => {
		setIsConfirmModalOpen(true);
		setRemoveExposure(exposure);
	};

	return (
		<>
			<StyledTitleContainer>
				<StyledTitle>{t('exposure.plural.upper')}</StyledTitle>
				{isInEditMode && renderActions()}
				{isConfirmModalOpen && renderRemoveExposureModal()}
			</StyledTitleContainer>
			<hr />
			<StyledSectionContainer>
				<ExposuresList
					exposures={exposures}
					openConfirmationModal={(exposure: Exposure) =>
						openConfirmationModal(exposure)
					}
				/>
			</StyledSectionContainer>
		</>
	);
};

export default withPolicyContext<Props>(Exposures);
